import React from 'react';
import { Badge, Button, Card, Col, Empty, List, Progress, Row, Space, Statistic, Table, Tag, Typography } from 'antd';
import { BarChartOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import { OWNER_ROLE_LABEL, fmtDate, fmtDateTime, stageLabel, useRecruitAccess } from './shared/recruitUi';

const { Text } = Typography;

type QueueProps = {
  title: string;
  rows: any[];
  empty: string;
  render: (r: any) => React.ReactNode;
  href: (r: any) => string;
  more?: string;
};

const Queue: React.FC<QueueProps> = ({ title, rows, empty, render, href, more }) => (
  <Card
    size="small"
    title={
      <Space>
        {title}
        <Badge count={rows.length} showZero color={rows.length ? '#fa8c16' : '#d9d9d9'} />
      </Space>
    }
    extra={more ? <Link to={more}>View all</Link> : undefined}
    style={{ height: '100%' }}
  >
    {rows.length ? (
      <List size="small" dataSource={rows} renderItem={(r) => <List.Item style={{ padding: '6px 0' }}><Link to={href(r)} style={{ width: '100%' }}>{render(r)}</Link></List.Item>} />
    ) : (
      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={empty} />
    )}
  </Card>
);

/** Talent Acquisition home: KPIs, personal counts, work queues and 90-day funnel. */
const RecruitHomePage: React.FC = () => {
  const navigate = useNavigate();
  const { canManage } = useRecruitAccess();
  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: hrmsKeys.recruitHome(),
    queryFn: () => hrmsService.getRecruitHome(),
    refetchInterval: 120_000,
  });

  const k = data?.kpis || {};
  const mine = data?.mine || {};
  const q = data?.queues || {};
  const funnel: Array<{ stage: string; count: number }> = data?.funnel_90d || [];
  const sources: Array<{ source: string; applications: number; hires: number }> = data?.sources_90d || [];
  const maxFunnel = Math.max(1, ...funnel.map((f) => f.count));

  const kpi = (title: string, value: number | undefined, to: string, warn = false) => (
    <Col xs={12} sm={8} lg={4} key={title}>
      <Card size="small" hoverable loading={isLoading} onClick={() => navigate(to)}>
        <Statistic title={title} value={value ?? 0} valueStyle={warn && value ? { color: '#cf1322' } : undefined} />
      </Card>
    </Col>
  );

  return (
    <WorkspaceLayout
      fullBleed
      header={
        <EnterprisePageHeader
          documentLabel="Talent Acquisition"
          subtitle="What needs attention today across requisitions, candidates, interviews, offers and onboarding"
          primaryActions={
            <Space>
              <Button icon={<ReloadOutlined />} loading={isFetching} onClick={() => refetch()} />
              <Button icon={<BarChartOutlined />} onClick={() => navigate('/hr/recruit-reports')}>
                Reports
              </Button>
              {canManage && (
                <>
                  <Button icon={<PlusOutlined />} onClick={() => navigate('/hr/candidates/new')}>
                    Candidate
                  </Button>
                  <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/hr/requisitions/new')}>
                    Requisition
                  </Button>
                </>
              )}
            </Space>
          }
        />
      }
    >
      <Row gutter={[12, 12]}>
        {kpi('Open requisitions', k.open_requisitions, '/hr/requisitions?status=Open')}
        {kpi('Awaiting approval', k.pending_approvals, '/hr/requisitions?status=PendingApproval')}
        {kpi('Published jobs', k.published_jobs, '/hr/jobs?status=Published')}
        {kpi('Active pipeline', k.active_pipeline, '/hr/applications')}
        {kpi('New applications (7d)', k.new_applications_7d, '/hr/applications')}
        {kpi('Interviews (next 7d)', k.interviews_next_7d, '/hr/interviews?view=calendar')}
        {kpi('Offers awaiting reply', k.offers_awaiting_response, '/hr/offers?status=Sent')}
        {kpi('Hires this month', k.hires_this_month, '/hr/applications?status=Hired')}
        {kpi('Onboarding in progress', k.onboarding_in_progress, '/hr/onboarding')}
        {kpi('Overdue onboarding tasks', k.overdue_onboarding_tasks, '/hr/reports/recruit-onboarding', true)}
      </Row>

      <Card size="small" style={{ marginTop: 12 }} title="My work">
        <Space size="large" wrap>
          <Link to="/hr/interviews/mine?scope=upcoming">
            <Statistic title="My upcoming interviews" value={mine.interviews_upcoming ?? 0} />
          </Link>
          <Link to="/hr/interviews/mine?scope=pending_feedback">
            <Statistic title="Feedback I owe" value={mine.feedback_pending ?? 0} valueStyle={mine.feedback_pending ? { color: '#cf1322' } : undefined} />
          </Link>
          <Link to="/hr/onboarding/my-tasks">
            <Statistic title="My onboarding tasks" value={mine.onboarding_tasks ?? 0} />
          </Link>
        </Space>
      </Card>

      <Row gutter={[12, 12]} style={{ marginTop: 12 }}>
        <Col xs={24} lg={8}>
          <Queue
            title="Waiting for screening (3+ days)"
            rows={q.awaiting_screening || []}
            empty="Nothing waiting"
            more="/hr/applications?status=Applied"
            href={(r) => `/hr/applications/${r.id}`}
            render={(r) => (
              <Space style={{ justifyContent: 'space-between', width: '100%' }}>
                <span>
                  {r.candidate_name} <Text type="secondary">· {r.job_title || '—'}</Text>
                </span>
                <Tag color="orange">{r.days_waiting}d</Tag>
              </Space>
            )}
          />
        </Col>
        <Col xs={24} lg={8}>
          <Queue
            title="Interviews in the next 48 hours"
            rows={q.upcoming_interviews || []}
            empty="No interviews scheduled"
            more="/hr/interviews?view=calendar"
            href={(r) => `/hr/interviews/${r.id}`}
            render={(r) => (
              <span>
                {fmtDateTime(r.scheduled_at)} — {r.candidate_name} <Text type="secondary">· R{r.round_number} {r.job_title || ''}</Text>
              </span>
            )}
          />
        </Col>
        <Col xs={24} lg={8}>
          <Queue
            title="Interviews missing feedback"
            rows={q.missing_feedback || []}
            empty="All feedback in"
            href={(r) => `/hr/interviews/${r.id}`}
            render={(r) => (
              <span>
                {fmtDate(r.scheduled_at)} — {r.candidate_name} <Text type="secondary">· {Number(r.feedback_count) || 0} scorecard(s)</Text>
              </span>
            )}
          />
        </Col>
        <Col xs={24} lg={8}>
          <Queue
            title="Pending approvals"
            rows={q.pending_approvals || []}
            empty="Nothing to approve"
            more="/home/approvals"
            href={(r) => (r.kind === 'offer' ? `/hr/offers/${r.id}` : `/hr/requisitions/${r.id}`)}
            render={(r) => (
              <span>
                <Tag>{r.kind === 'offer' ? 'Offer' : 'Requisition'}</Tag>
                {r.number} <Text type="secondary">· {r.title}</Text>
              </span>
            )}
          />
        </Col>
        <Col xs={24} lg={8}>
          <Queue
            title="Offers expiring within 3 days"
            rows={q.expiring_offers || []}
            empty="No offers expiring"
            more="/hr/offers?status=Sent"
            href={(r) => `/hr/offers/${r.id}`}
            render={(r) => (
              <span>
                {r.offer_number} — {r.candidate_name} <Text type="danger">· expires {fmtDate(r.offer_expires_at)}</Text>
              </span>
            )}
          />
        </Col>
        <Col xs={24} lg={8}>
          <Queue
            title="Accepted — create employee"
            rows={q.accepted_not_converted || []}
            empty="No accepted offers waiting"
            more="/hr/offers?status=Accepted"
            href={(r) => `/hr/offers/${r.id}/convert`}
            render={(r) => (
              <span>
                {r.candidate_name} <Text type="secondary">· joins {fmtDate(r.join_date)}</Text>
              </span>
            )}
          />
        </Col>
        <Col xs={24}>
          <Card size="small" title="Overdue onboarding tasks" extra={<Link to="/hr/reports/recruit-onboarding">Onboarding report</Link>}>
            <Table
              size="small"
              rowKey="id"
              pagination={false}
              dataSource={q.overdue_onboarding_tasks || []}
              locale={{ emptyText: 'No overdue tasks' }}
              onRow={(r: any) => ({ onClick: () => navigate(`/hr/onboarding/${r.plan_id}`), style: { cursor: 'pointer' } })}
              columns={[
                { title: 'New hire', dataIndex: 'employee_name' },
                { title: 'Task', dataIndex: 'task_name' },
                { title: 'Owner', key: 'o', render: (_, r: any) => r.owner_name || OWNER_ROLE_LABEL[r.owner_role] || r.owner_role || '—' },
                { title: 'Due', dataIndex: 'due_date', width: 120, render: (v) => <Text type="danger">{fmtDate(v)}</Text> },
              ]}
            />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card size="small" title="Pipeline — applications in the last 90 days" extra={<Link to="/hr/reports/recruit-funnel">Funnel report</Link>} loading={isLoading}>
            {funnel.map((f) => (
              <Row key={f.stage} align="middle" gutter={8} style={{ marginBottom: 4 }}>
                <Col span={6}>
                  <Link to={`/hr/applications?status=${f.stage}`}>{stageLabel(f.stage)}</Link>
                </Col>
                <Col span={15}>
                  <Progress percent={Math.round((f.count / maxFunnel) * 100)} showInfo={false} size="small" />
                </Col>
                <Col span={3} style={{ textAlign: 'right' }}>
                  {f.count}
                </Col>
              </Row>
            ))}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card size="small" title="Sources — last 90 days" extra={<Link to="/hr/reports/recruit-sources">Source report</Link>} loading={isLoading}>
            <Table
              size="small"
              rowKey="source"
              pagination={false}
              dataSource={sources}
              locale={{ emptyText: 'No applications yet' }}
              columns={[
                { title: 'Source', dataIndex: 'source' },
                { title: 'Applications', dataIndex: 'applications', width: 110, align: 'right' },
                { title: 'Hires', dataIndex: 'hires', width: 80, align: 'right' },
              ]}
            />
          </Card>
        </Col>
      </Row>
    </WorkspaceLayout>
  );
};

export default RecruitHomePage;
